/**
 * Minimal robust POST.
 *
 * Always uses same-origin (window.location.origin) because the site is served
 * behind an ingress that routes /api/* to the backend on the SAME domain.
 * This works in preview AND production regardless of REACT_APP_BACKEND_URL.
 *
 * Dual transport fallback:
 *   1. fetch (standard)
 *   2. XMLHttpRequest (bypasses any 3rd-party script that wraps fetch)
 *
 * Returns { ok, status, text, json }. Never throws on HTTP status — only on total network failure.
 */

function normalize(status, text) {
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { /* keep raw */ }
  }
  return { ok: status >= 200 && status < 300, status, text, json: data };
}

export async function robustPost(path, body, { timeoutMs = 15000 } = {}) {
  const jsonBody = typeof body === 'string' ? body : JSON.stringify(body);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = (typeof window !== 'undefined' ? window.location.origin : '') + cleanPath;
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

  // Attempt 1: fetch
  try {
    if (typeof fetch === 'function') {
      const res = await fetch(url, { method: 'POST', headers, body: jsonBody });
      let text = '';
      try { text = await res.clone().text(); } catch { try { text = await res.text(); } catch {} }
      return normalize(res.status, text);
    }
  } catch (e) {
    // fall through to XHR
  }

  // Attempt 2: XMLHttpRequest (not wrapped by 3rd-party scripts)
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.timeout = timeoutMs;
      xhr.onload = () => resolve(normalize(xhr.status, xhr.responseText || ''));
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timeout'));
      xhr.send(jsonBody);
    } catch (e) {
      reject(e);
    }
  });
}
